import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { ArrowRight } from "lucide-react";

const REASONS = {
  expired: "Your session expired. Please log in again.",
  signed_out: "You were signed out. Please log in again.",
  removed: "You no longer have access to that organization. Please log in again.",
};

export default function Login() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const msg = REASONS[params.get("reason")];
    if (msg) toast.info(msg, { id: "session-reason" });
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/app");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-grid bg-grid-fade opacity-30 pointer-events-none" />
      <Card className="relative w-full max-w-md bg-slate-900/90 border-slate-800 p-8 rounded-2xl">
        <div className="mb-8 flex justify-center"><Logo size="lg" /></div>
        <h1 className="font-display text-2xl font-bold mb-2">Welcome back</h1>
        <p className="text-sm text-slate-400 mb-6">Log in to your RoofLead command center.</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="email" className="text-slate-300">Email</Label>
            <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 bg-slate-800 border-slate-700 text-white" required />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="password" className="text-slate-300">Password</Label>
              <Link to="/forgot-password" data-testid="login-forgot-link" className="text-xs text-orange-400 hover:text-orange-300 font-medium">
                Forgot password?
              </Link>
            </div>
            <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 bg-slate-800 border-slate-700 text-white" required />
          </div>
          <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold h-11">
            {loading ? "Logging in…" : <>Log in <ArrowRight className="w-4 h-4 ml-1" /></>}
          </Button>
        </form>
        <p className="text-sm text-slate-400 mt-6 text-center">
          No account? <Link to="/signup" className="text-orange-400 hover:text-orange-300 font-medium" data-testid="login-goto-signup">Create an account</Link>
        </p>
      </Card>
    </div>
  );
}
